/**
 * Visit entity DTOs
 * Extracted from src/types/patientmr/visit.ts - API contract types only
 */

import { Timestamps } from '../common/base.dto';

/**
 * Visit status — aligned with backend VISIT_STATUS enum (COMPLETE added in Phase 18 for supervisory visits).
 * COMPLETED does not exist in the backend and must not be used.
 */
export type VisitStatus =
  | 'SCHEDULED'
  | 'INPROGRESS'
  | 'DOCPENDING'
  | 'READYFORBILLING'
  /** Phase 18 M13: a finalized supervisory visit (not billed; reopen returns it to DOCPENDING) */
  | 'COMPLETE'
  | 'BILLED'
  | 'CANCELLED';

/**
 * Reason codes for visit cancellation (backend VISIT_CANCELLATION_REASON enum).
 * When reason is 'OTHER', cancellationNote is required.
 */
export type VisitCancellationReason =
  | 'PATIENT_UNAVAILABLE'
  | 'CLINICIAN_UNAVAILABLE'
  | 'WEATHER'
  | 'HOSPITALIZED'
  | 'OTHER';

/**
 * @deprecated Use VisitStatusPill / getVisitStatusConfig from common/Pills instead.
 */
export const VISIT_STATUS_BADGE: Record<VisitStatus, { label: string; className: string }> = {
  SCHEDULED:       { label: 'Scheduled',        className: 'bg-violet-100 text-violet-700 border-violet-300 font-semibold text-xs rounded border' },
  INPROGRESS:      { label: 'In Progress',      className: 'bg-blue-100 text-blue-800 border-blue-200 font-semibold text-xs rounded border' },
  DOCPENDING:      { label: 'Doc Pending',      className: 'bg-amber-100 text-amber-800 border-amber-300 font-semibold text-xs rounded border' },
  READYFORBILLING: { label: 'Ready for Billing', className: 'bg-emerald-100 text-emerald-800 border-emerald-300 font-semibold text-xs rounded border' },
  BILLED:          { label: 'Billed',           className: 'bg-slate-200 text-slate-700 border-slate-400 font-semibold text-xs rounded border' },
  COMPLETE:        { label: 'Complete',         className: 'bg-teal-100 text-teal-800 border-teal-300 font-semibold text-xs rounded border' },
  CANCELLED:       { label: 'Cancelled',        className: 'bg-stone-100 text-stone-600 border-stone-200 font-semibold text-xs rounded border' },
};

/**
 * Visit entity as received from the backend API
 */
export interface Visit extends Timestamps {
  id: string;
  visitId?: string;
  _id?: string;
  visit_id?: string;
  patientId: string;
  providerId: string;
  tenantId: string;
  startTime: string;
  endTime?: string;
  authorizationId?: string;
  authorizationNumber?: string;
  notes?: string;
  signature?: string;
  status: VisitStatus;
  visitDate?: string;
  discipline?: string;
  patientName?: string;
  providerName?: string;
  /** Phase 9C: chart status and worst charted incident severity (CHT-08) */
  chartingStatus?: string;
  maxIncidentSeverity?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancellationReason?: VisitCancellationReason;
  // EVV snapshot taken at visit start (R-177: payer AND service)
  serviceType?: string;
  /** Claims need a matching EVV visit */
  evvRequired?: boolean;
  /** Clinician is prompted to clock in/out on HHAX (required visits, and PDN when the agency collects) */
  evvCollect?: boolean;
  evvStatus?: string;
  /** Phase 18 M13: Routine / Initial / … / Supervisory (RN only, never billed) */
  visitType?: string;
  /** Phase 18 M4: who signed at the end of the visit, or why nobody could */
  signerName?: string;
  signerRelationship?: SignerRelationship;
  signedBy?: string;
  signatureUnavailableReason?: string;
  noCareReason?: NoCareReason;
  /** Phase 18 M2: chart corrections allowed until this time (set by a reopen) */
  correctionWindowUntil?: string;
}

/** Phase 18 M4: the visit ended without care (no chart entries needed). */
export type NoCareReason = 'PATIENT_UNAVAILABLE' | 'HOSPITALIZED' | 'CAREGIVER_DECLINED' | 'OTHER';
export const NO_CARE_REASONS: { value: NoCareReason; label: string }[] = [
  { value: 'PATIENT_UNAVAILABLE', label: 'Patient unavailable' },
  { value: 'HOSPITALIZED', label: 'Patient hospitalized' },
  { value: 'CAREGIVER_DECLINED', label: 'Caregiver declined care' },
  { value: 'OTHER', label: 'Other' },
];
/** Minimum length of `noCareNote` */
export const NO_CARE_NOTE_MIN = 10;

/** Phase 18 M4: the end-of-visit signer's relationship to the patient. */
export type SignerRelationship = 'PATIENT' | 'PARENT' | 'GUARDIAN' | 'CAREGIVER' | 'OTHER';
export const SIGNER_RELATIONSHIPS: { value: SignerRelationship; label: string }[] = [
  { value: 'PATIENT', label: 'Patient' },
  { value: 'PARENT', label: 'Parent' },
  { value: 'GUARDIAN', label: 'Guardian' },
  { value: 'CAREGIVER', label: 'Caregiver' },
  { value: 'OTHER', label: 'Other' },
];
/** Minimum length of `signatureUnavailableReason` */
export const SIGNATURE_UNAVAILABLE_MIN = 5;

/**
 * Phase 18 M4: end-visit body additions (POST /patientmr/{patientId}/visits/{visitId}/end).
 * Either `signature` + `signerName` + `signerRelationship`, or `signatureUnavailableReason`.
 */
export interface EndVisitSignoff {
  signature?: string;
  signerName?: string;
  signerRelationship?: SignerRelationship;
  signatureUnavailableReason?: string;
  noCareReason?: NoCareReason;
  noCareNote?: string;
}

/** Phase 18 M13: the visit type value sent for a supervisory visit */
export const SUPERVISORY_VISIT_TYPE = 'Supervisory';

/**
 * Request body for POST /patientmr/{patientId}/visits/{visitId}/cancel
 */
export interface CancelVisitRequest {
  cancellationReason: VisitCancellationReason;
  /** Required when cancellationReason is 'OTHER' */
  cancellationNote?: string;
}

/**
 * Visit history response with pagination
 */
export interface VisitHistoryResponse {
  visits: Visit[];
  paginationToken?: string;
  hasMore: boolean;
  totalCount?: number;
}

/**
 * Payload for starting a new visit
 */
export interface StartVisitPayload {
  patientId: string;
  authorizationId?: string;
  startTime: string;
  visitDate?: string;
  discipline?: string;
  geolocation?: {
    latitude: number;
    longitude: number;
    timestamp: string;
  };
}

/** Phase 18 M9: who the visit note PDF is for. External leaves out INTERNAL entries. */
export type VisitNoteAudience = 'internal' | 'external';
/** Purpose note bounds for an external copy (Phase 14 pattern) */
export const VISIT_NOTE_PURPOSE_MIN = 10;
export const VISIT_NOTE_PURPOSE_MAX = 500;

/** GET /patientmr/{patientId}/visits/{visitId}/chart/pdf response: a 5-minute download link */
export interface VisitNotePdfLink {
  url: string;
  fileName: string;
  expiresIn: number;
  audience: VisitNoteAudience;
}
